import React from 'react';
import { useDOMPurify } from '../../lib/useDOMPurify';
import { TextImageBlockData } from '../../types/optimizely/blocks/textImageBlock';
import {
	getBackgroundColorClassName,
	getFontColorClassName,
} from '../../lib/getColorClassNames';

interface TextImageBlockProps {
	reversed?: boolean;
	content: string;
	title: string;
	backgroundColor: string;
	fontColor: string;
	imageSrc: string;
	className?: string;
	id?: string;
	isHeroBlock?: boolean;
}

const mapTextImageBlockToTextImageBlockProps = (
	block: TextImageBlockData,
): TextImageBlockProps => {
	return {
		reversed: block.imagePosition.value === 'Right',
		title: block.title.value,
		content: block.textEditor.value,
		backgroundColor: block.backgroundColor.value,
		fontColor: block.fontColor.value,
		imageSrc: block.image.value?.url,
	};
};

export const TextImageBlock: React.FC<TextImageBlockProps> = ({
	title,
	reversed,
	content,
	backgroundColor,
	fontColor,
	isHeroBlock,
	imageSrc,
	className,
	id,
}) => {
	const sanitizedContent = useDOMPurify(content);

	const mdFlexClass = ` ${reversed ? 'md:flex-row-reverse' : 'md:flex-row'}`;

	const paddingClass = `${isHeroBlock ? 'pt-20 md:pt-22 pb-0 md:pb-0' : ''}`;

	const backgroundColorClass = getBackgroundColorClassName(backgroundColor);
	const fontColorClass = getFontColorClassName(fontColor);

	return (
		<section
			className={`p-8 px-0 md:py-32 ${backgroundColorClass} ${fontColorClass} ${paddingClass} ${className ?? ''}`}
			id={id}
		>
			<div className={`container flex flex-col-reverse ${mdFlexClass} gap-12`}>
				<div className="md:w-1/2">
					<img
						src={imageSrc}
						alt={title}
						className="h-full w-full object-contain"
					/>
				</div>
				<div className="md:w-1/2 flex flex-col justify-center">
					<div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
				</div>
			</div>
		</section>
	);
};

export const TextImageBlockContainter: React.FC<TextImageBlockData> = (
	block,
) => {
	const { title, reversed, content, backgroundColor, fontColor, imageSrc } =
		mapTextImageBlockToTextImageBlockProps(block);

	return (
		<TextImageBlock
			title={title}
			reversed={reversed}
			content={content}
			backgroundColor={backgroundColor}
			fontColor={fontColor}
			imageSrc={imageSrc}
			isHeroBlock={block.isHeroBlock}
			id={block.isHeroBlock ? undefined : block.contentLink.id}
		/>
	);
};
